// HO-TASK-250: mapa de rutas -> etiqueta de miga de pan.
//
// Cada entrada describe un prefijo de URL (patrón de react-router) y el texto
// que debe mostrar la miga cuando la URL actual pasa por ese prefijo. El
// resolutor (resolveBreadcrumbs) recorre la URL segmento a segmento y busca la
// PRIMERA entrada que coincida exactamente con cada prefijo, así que lo
// específico va antes que lo genérico dentro de un mismo módulo.
//
// - `label: null` = el prefijo existe pero no aporta un nivel propio (por
//   ejemplo /dashboard/finance/12 es la misma pantalla que /dashboard/finance,
//   solo que con el condominio en la URL). Un prefijo sin entrada se comporta
//   igual: se omite (ids sueltos, segmentos intermedios como "edit" o "my").
// - `label` puede ser una función que recibe { role, params, state } cuando el
//   nombre cambia según quién navega (el propietario ve "Sugerencias", el
//   manager "Centro de sugerencias") o según el location.state con el que se
//   llegó (los gastos muestran el tipo: "Gastos extraordinarios").
// - `to` sobreescribe el destino del link cuando el prefijo por sí solo no es
//   una ruta navegable (ej. .../receivable existe solo como .../receivable/properties).
//   Se compila con generatePath usando los params del match, así que va con
//   `:id` plano, sin la restricción (\d+). También puede ser una función de
//   { role, params } cuando el destino depende de quién navega.
// - `parents` ({ role, params, state }) => [{ label, to }] agrega niveles que
//   no aparecen en la URL pero sí en el recorrido: desde "Efectos de la
//   propiedad" se llega a pagar cuentas y al detalle de un efecto, y esas
//   URLs no llevan la propiedad (o la llevan en otro segmento), así que se
//   reconstruye el nivel a partir de los params o del location.state.

const ID = ":id(\\d+)";

// Pantallas del servicio contratado con Homelan; cuelgan de finance,
// accounting y funds con el condominio delante.
const serviceRoutes = (base) => [
  { path: `${base}/:condominiumId(\\d+)/my/service`, label: "Estado de mi servicio" },
  { path: `${base}/:condominiumId(\\d+)/my/service/:charge`, label: "Pagos del servicio" },
  { path: `${base}/:condominiumId(\\d+)/my/service/:charge/payment`, label: "Emisión de pago" },
  { path: `${base}/:condominiumId(\\d+)/my/service/:charge/pay`, label: "Emisión de servicio" },
  { path: `${base}/:condominiumId(\\d+)/my/service/:charge/:paymentId(\\d+)`, label: "Detalle de pago" },
];

const advanceLabel = ({ role }) =>
  role === "property" ? "Mis saldos a favor" : "Saldos a favor de propietarios";

// Hijos que Finanzas y Contabilidad comparten tal cual (FinanceRoutes y
// AccountingRoutes declaran las mismas rutas bajo distinto prefijo).
const financeLikeChildren = (base) => [
  { path: `${base}/${ID}`, label: null },
  { path: `${base}/advance`, label: advanceLabel },
  { path: `${base}/${ID}/advance/add`, label: "Registro de saldo a favor" },

  { path: `${base}/${ID}/receivable`, label: "Efectos por cobrar", to: `${base}/:id/receivable/properties` },
  { path: `${base}/${ID}/receivable/properties`, label: null },
  { path: `${base}/${ID}/receivable/create`, label: "Registrar efecto por cobrar" },
  {
    path: `${base}/${ID}/receivable/detail/:debtReceivedId(\\d+)`,
    label: "Detalle de efecto por cobrar",
    // ReceivableProperties manda `property` en el state al abrir el detalle.
    parents: ({ state }) =>
      state?.property
        ? [{ label: "Estado de cuenta", to: `${base}/:id/receivable/${state.property}` }]
        : [],
  },
  {
    path: `${base}/${ID}/receivable/topays/:properties(\\d+)`,
    label: "Cuentas por pagar de la propiedad",
    parents: () => [{ label: "Estado de cuenta", to: `${base}/:id/receivable/:properties` }],
  },
  {
    path: `${base}/${ID}/receivable/property/:properties(\\d+)/pay/:pay(\\d+)`,
    label: "Emisión de pago",
    parents: () => [
      { label: "Estado de cuenta", to: `${base}/:id/receivable/:properties` },
      { label: "Cuentas por pagar de la propiedad", to: `${base}/:id/receivable/topays/:properties` },
    ],
  },
  { path: `${base}/${ID}/receivable/:property(\\d+)`, label: "Estado de cuenta" },

  { path: `${base}/${ID}/topay`, label: "Cuentas por pagar" },
  { path: `${base}/${ID}/topay/create`, label: "Registrar cuenta por pagar" },
  { path: `${base}/${ID}/topay/edit/:debtToPayId(\\d+)`, label: "Detalle de cuenta por pagar" },
  { path: `${base}/${ID}/topay-provider`, label: "Cuentas por pagar", to: `${base}/:id/topay` },
  { path: `${base}/${ID}/topay-provider/edit/:debtToPayId(\\d+)`, label: "Detalle de cuenta por pagar" },
  { path: `${base}/${ID}/topay-provider/edit/:debtToPayId(\\d+)/payment`, label: "Emisión de pago" },

  { path: `${base}/${ID}/payments-provider`, label: "Pagos a proveedores" },
  { path: `${base}/${ID}/payments-provider/:provider(\\d+)`, label: "Cuentas del proveedor" },
  { path: `${base}/${ID}/payments-provider-paid`, label: "Pagos realizados a proveedores" },

  { path: `${base}/${ID}/aliquots`, label: "Alícuotas" },
  { path: `${base}/${ID}/aliquots/create`, label: "Asignar alícuota" },
  { path: `${base}/${ID}/aliquots/edit/:aliquotId(\\d+)`, label: "Editar alícuota" },

  { path: `${base}/${ID}/individual-costs`, label: ({ state }) => state?.type_name || "Gastos particulares" },
  { path: `${base}/${ID}/individual-costs/create`, label: "Crear gasto particular" },
  { path: `${base}/${ID}/individual-costs/edit/:individualId(\\d+)`, label: "Editar gasto particular" },

  { path: `${base}/${ID}/common-costs`, label: ({ state }) => state?.type_name || "Gastos comunes" },
  { path: `${base}/${ID}/common-costs/create`, label: "Crear gasto común" },
  { path: `${base}/${ID}/common-costs/edit/:commonId(\\d+)`, label: "Editar gasto común" },

  { path: `${base}/${ID}/payment-reserve-fund/create`, label: "Transferencia a fondo de reserva" },

  { path: `${base}/${ID}/common-income`, label: "Ingresos comunes" },
  { path: `${base}/${ID}/common-income/create`, label: "Añadir ingreso común" },
  { path: `${base}/${ID}/individual-income`, label: "Ingresos individuales" },
  { path: `${base}/${ID}/individual-income/create`, label: "Añadir ingreso individual" },

  { path: `${base}/${ID}/reserve-funds`, label: "Fondos de reserva" },
  { path: `${base}/${ID}/reserve-funds/create`, label: "Añadir fondo de reserva" },
  { path: `${base}/${ID}/reserve-funds/:reserveFundId(\\d+)`, label: "Detalle del fondo de reserva" },
  { path: `${base}/${ID}/payment-fund/create`, label: "Emitir cobro para fondo" },

  // El propietario ve solo sus propios pagos (.../payments/property); el
  // listado general es del manager, así que para él este nivel no existe.
  {
    path: `${base}/${ID}/payments`,
    label: ({ role }) => (role === "property" ? null : "Pagos de propietarios"),
    to: `${base}/:id/payments/properties`,
  },
  { path: `${base}/${ID}/payments/properties`, label: null },
  { path: `${base}/${ID}/payments/property`, label: "Mis pagos" },
  { path: `${base}/${ID}/payments/property/:propertyPayment(\\d+)`, label: "Detalle de pago" },
  { path: `${base}/${ID}/payments/:property(\\d+)`, label: "Pagos del propietario" },
  { path: `${base}/${ID}/payment/:propertyPayment(\\d+)`, label: "Detalle de pago" },
  { path: `${base}/${ID}/pay/:propertyPayment(\\d+)`, label: "Detalle de pago" },

  { path: `${base}/${ID}/reports`, label: "Reportes" },
  { path: `${base}/${ID}/reports/properties`, label: "Deuda acumulada" },
  { path: `${base}/${ID}/form`, label: "Previsualización de planilla" },

  { path: `${base}/${ID}/accounts`, label: "Plan de cuentas" },
  { path: `${base}/${ID}/account-plans`, label: "Plan de cuentas" },
  { path: `${base}/${ID}/ledger`, label: "Libro Mayor" },
  { path: `${base}/${ID}/journal`, label: "Libro Diario" },
  { path: `${base}/${ID}/account-movements/:accountId(\\d+)`, label: "Movimientos de cuenta" },

  ...serviceRoutes(base),
];

// Fondos de reserva tiene su propio árbol (ReserveFundRoutes), más corto y con
// nombres distintos para algunos hijos.
const fundsChildren = (base) => [
  { path: `${base}/${ID}`, label: null },
  { path: `${base}/advance`, label: advanceLabel },

  { path: `${base}/${ID}/receivable`, label: "Efectos por cobrar" },
  { path: `${base}/${ID}/receivable/create`, label: "Registrar efecto por cobrar" },
  { path: `${base}/${ID}/receivable/detail/:debtReceivedId(\\d+)`, label: "Detalle de efecto por cobrar" },

  { path: `${base}/${ID}/topay`, label: "Cuentas por pagar" },
  { path: `${base}/${ID}/topay/create`, label: "Registrar cuenta por pagar" },
  { path: `${base}/${ID}/topay/edit/:debtToPayId(\\d+)`, label: "Detalle de cuenta por pagar" },

  { path: `${base}/${ID}/aliquots`, label: "Alícuotas" },
  { path: `${base}/${ID}/aliquots/create`, label: "Asignar alícuota" },
  { path: `${base}/${ID}/aliquots/edit/:aliquotId(\\d+)`, label: "Editar alícuota" },

  { path: `${base}/${ID}/common-costs`, label: "Gastos comunes" },
  { path: `${base}/${ID}/common-costs/create`, label: "Crear gasto común" },
  { path: `${base}/${ID}/common-income`, label: "Ingresos comunes" },
  { path: `${base}/${ID}/common-income/create`, label: "Añadir ingreso común" },

  { path: `${base}/${ID}/reserve`, label: "Listado de fondos" },
  { path: `${base}/${ID}/reserve/create`, label: "Añadir fondo de reserva" },
  { path: `${base}/${ID}/cash`, label: "Caja" },
  { path: `${base}/${ID}/cash/create`, label: "Emitir cobro para fondo" },

  { path: `${base}/${ID}/payments`, label: "Pagos de propietarios" },
  { path: `${base}/${ID}/payments/:propertyPayment(\\d+)`, label: "Detalle de pago" },

  ...serviceRoutes(base),
];

const dashboardRoutes = [
  { path: "/dashboard", label: "Inicio", to: "/dashboard/app" },
  { path: "/dashboard/app", label: null },

  { path: "/dashboard/condominiums", label: "Condominios" },
  { path: "/dashboard/condominiums/create", label: "Crear condominio" },
  { path: `/dashboard/condominiums/${ID}`, label: "Editar condominio" },

  { path: "/dashboard/exchange", label: "Tasas de cambio" },
  { path: "/dashboard/currency", label: "Moneda" },
  { path: "/dashboard/config", label: "Configuración" },
  { path: "/dashboard/notifications", label: "Notificaciones" },
  { path: "/dashboard/faqs", label: "Preguntas frecuentes" },
  { path: "/dashboard/feed", label: "Muro de noticias" },
  { path: "/dashboard/release", label: "Comunicados" },
  { path: "/dashboard/renter", label: "Inquilinos" },

  { path: "/dashboard/banks", label: "Bancos" },
  { path: `/dashboard/banks/${ID}`, label: null },
  { path: "/dashboard/banks/search/:all", label: "Búsqueda" },
  { path: `/dashboard/banks/create/${ID}`, label: "Crear banco" },
  { path: `/dashboard/banks/edit/${ID}`, label: "Editar banco" },

  { path: "/dashboard/periods", label: "Períodos" },
  { path: `/dashboard/periods/create/${ID}`, label: "Crear período" },
  { path: `/dashboard/periods/edit/${ID}`, label: "Editar período" },

  { path: "/dashboard/finance", label: "Finanzas" },
  ...financeLikeChildren("/dashboard/finance"),
  { path: "/dashboard/accounting", label: "Contabilidad" },
  ...financeLikeChildren("/dashboard/accounting"),
  { path: "/dashboard/funds", label: "Fondos de reserva" },
  ...fundsChildren("/dashboard/funds"),
  // /dashboard/aliquots redirige a /dashboard/finance/:id/aliquots.
  { path: "/dashboard/aliquots", label: "Alícuotas" },

  // Área personal del propietario.
  { path: "/dashboard/my/profile", label: "Mi perfil" },
  { path: "/dashboard/my/finance", label: "Mis finanzas" },
  { path: "/dashboard/my/finance/topay", label: "Estado de cuenta" },
  { path: `/dashboard/my/finance/topay/${ID}`, label: "Detalle de cuenta por pagar" },
  { path: `/dashboard/my/finance/topay/${ID}/payment`, label: "Emisión de pago" },
  { path: "/dashboard/my/finance/topays", label: "Detalle de cuentas por pagar" },
  { path: "/dashboard/my/finance/pays", label: "Mis pagos" },
  { path: "/dashboard/my/finance/pays/:propertyPayment(\\d+)", label: "Detalle de pago" },
  { path: "/dashboard/my/finance/advance", label: "Mis saldos a favor" },
  { path: "/dashboard/my/finance/advance/add", label: "Registro de saldo a favor" },
  { path: "/dashboard/my/finance/reports", label: "Reportes" },

  { path: "/dashboard/properties", label: "Propietarios" },
  { path: "/dashboard/properties/:property(\\d+)", label: "Información del propietario" },
  { path: "/dashboard/properties/:property(\\d+)/visitors", label: "Visitantes" },
  { path: "/dashboard/properties/advance/:property(\\d+)", label: "Saldos a favor del propietario" },
  { path: "/dashboard/properties/individual-costs/create/:property(\\d+)", label: "Registrar cuenta por cobrar" },
  { path: "/dashboard/properties/edit/:property(\\d+)", label: "Editar propietario" },

  { path: "/dashboard/providers", label: "Proveedores" },
  { path: "/dashboard/providers/create", label: "Crear proveedor" },
  { path: "/dashboard/providers/edit/:provider(\\d+)", label: "Editar proveedor" },
  { path: `/dashboard/providers/${ID}/payments-provider/:provider(\\d+)`, label: "Cuentas por pagar del proveedor" },

  { path: "/dashboard/security", label: "Seguridad" },
  { path: "/dashboard/security/vigilants", label: "Vigilantes" },
  { path: "/dashboard/security/vigilants/create", label: "Crear vigilante" },
  { path: "/dashboard/security/vigilants/:authId(\\d+)/edit", label: "Editar vigilante" },
  { path: `/dashboard/security/vigilants/${ID}`, label: "Detalle de vigilante" },

  { path: "/dashboard/visitors", label: ({ role }) => (role === "manager" ? "Visitantes" : "Visitas") },
  { path: "/dashboard/visitors/create", label: "Registrar visitante" },
  { path: "/dashboard/visitors/history", label: "Historial de visitas" },
  { path: `/dashboard/visitors/${ID}`, label: "Detalle de visitante" },

  { path: "/dashboard/support", label: "Soporte" },
  { path: "/dashboard/support/create", label: "Crear ticket" },
  { path: `/dashboard/support/${ID}`, label: "Ticket de soporte" },

  { path: "/dashboard/complaints", label: ({ role }) => (role === "manager" ? "Centro de sugerencias" : "Sugerencias") },
  { path: "/dashboard/complaints/create", label: "Crear sugerencia" },
  { path: `/dashboard/complaints/${ID}`, label: "Detalle de sugerencia" },

  { path: "/dashboard/rewards", label: "Homelan Rewards" },
  { path: "/dashboard/marketplace", label: "Marketplace" },
  { path: "/dashboard/marketplace/services", label: null },
  { path: `/dashboard/marketplace/services/${ID}`, label: "Detalle del servicio" },
  { path: "/dashboard/marketplace/requests", label: "Mis solicitudes" },
  { path: `/dashboard/marketplace/requests/${ID}`, label: "Conversación" },

  { path: "/dashboard/surveys", label: "Encuestas/Votaciones" },
  { path: "/dashboard/surveys/create/:survey(\\d+)", label: "Crear encuesta/votación" },
  { path: `/dashboard/surveys/edit/${ID}`, label: "Editar encuesta/votación" },
  { path: `/dashboard/surveys/preview/${ID}`, label: "Resultados" },
  { path: "/dashboard/surveys/:voting(\\d+)", label: "Votación" },

  // El propietario no tiene acceso a /dashboard/parking (es de manager y
  // seguridad): su raíz del módulo es el listado de sus puestos.
  {
    path: "/dashboard/parking",
    label: ({ role }) => (role === "property" ? "Mis parqueos" : "Parqueos"),
    to: ({ role }) => (role === "property" ? "/dashboard/parking/my/stalls" : "/dashboard/parking"),
  },
  { path: "/dashboard/parking/create", label: "Crear parqueo" },
  { path: `/dashboard/parking/${ID}/edit`, label: "Editar parqueo" },
  { path: `/dashboard/parking/${ID}/stalls`, label: "Puestos" },
  { path: `/dashboard/parking/stalls/${ID}`, label: "Detalle de puesto" },
  // El propietario entra a /dashboard/parking/my/stalls directo desde el
  // menú ("Mis parqueos"), así que /dashboard/parking ya es ese nivel.
  { path: "/dashboard/parking/my/stalls", label: null },
  { path: `/dashboard/parking/my/stalls/${ID}`, label: "Detalle de puesto" },

  { path: "/dashboard/areas", label: "Áreas comunes" },
  { path: "/dashboard/areas/create", label: "Crear área común" },
  { path: `/dashboard/areas/${ID}/edit`, label: "Editar área común" },
  { path: `/dashboard/areas/${ID}/reservations`, label: "Reservaciones" },
];

const rootRoutes = [
  { path: "/root", label: "Inicio" },

  { path: "/root/condominiums", label: "Condominios" },
  { path: "/root/condominiums/create", label: "Crear condominio" },
  { path: `/root/condominiums/${ID}/edit`, label: "Editar condominio" },
  { path: `/root/condominiums/${ID}/service`, label: "Emisión de servicio" },
  { path: `/root/condominiums/${ID}/status`, label: "Estado de servicio" },
  { path: `/root/condominiums/${ID}/payments/:charge`, label: "Servicio contratado" },
  { path: `/root/condominiums/${ID}/payments/:charge/:paymentId(\\d+)`, label: "Detalle de pago" },

  { path: "/root/indicators", label: "Indicadores" },
  { path: `/root/indicators/${ID}/payments`, label: "Pagos de propietarios", to: "/root/indicators/:id/payments/properties" },
  { path: `/root/indicators/${ID}/payments/properties`, label: null },
  { path: `/root/indicators/${ID}/payments/:property(\\d+)`, label: "Pagos del propietario" },

  { path: "/root/exchange", label: "Tasas de cambio" },
  { path: "/root/paymethods", label: "Métodos de pago" },
  { path: "/root/notifications", label: "Notificaciones" },
  { path: "/root/config", label: "Configuración de precios" },
  { path: "/root/lead", label: "Leads" },
  { path: "/root/account-deletion", label: "Eliminación de cuentas" },

  { path: "/root/support", label: "Soporte" },
  { path: "/root/marketplace", label: "Marketplace" },
  { path: "/root/marketplace/providers", label: null },
  { path: `/root/marketplace/providers/${ID}`, label: "Revisión de proveedor" },
  { path: "/root/marketplace/categories", label: "Categorías" },
];

const providerRoutes = [
  { path: "/provider", label: "Mis servicios" },
  { path: "/provider/services", label: null },
  { path: "/provider/services/create", label: "Publicar servicio" },
  { path: `/provider/services/${ID}`, label: null },
  { path: `/provider/services/${ID}/edit`, label: "Editar servicio" },
  { path: "/provider/profile", label: "Perfil y verificación" },
  { path: "/provider/requests", label: "Solicitudes" },
  { path: `/provider/requests/${ID}`, label: "Conversación" },
];

const breadcrumbRoutes = [...dashboardRoutes, ...rootRoutes, ...providerRoutes];

export default breadcrumbRoutes;
